import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { FileText } from "lucide-react";
import DocumentTableSkeleton, {
  type DocumentTableSkeletonColumn,
} from "./DocumentTableSkeleton";
import EmptyState from "./EmptyState";
import TableError from "./TableError";

export type CleTableColumn<TData> = {
  cell: (item: TData) => ReactNode;
  className?: string;
  header: ReactNode;
  id: string;
  title?: (item: TData) => string | undefined;
};

export type CleTableSelection<TData> = {
  getAriaLabel: (item: TData) => string;
  isDisabled?: (item: TData) => boolean;
  onSelectAll?: (itemIds: string[]) => void;
  onToggle: (itemId: string) => void;
  selectedIds: string[];
  selectAllAriaLabel?: string;
};

export type CleTableRowAction<TData> = {
  className?: string | ((item: TData) => string | undefined);
  isDisabled?: (item: TData) => boolean;
  onClick: (itemId: string, item: TData) => void;
};

export type CleTableProps<TData extends { id: string }> = {
  columns: CleTableColumn<TData>[];
  data: TData[];
  emptyDescription?: ReactNode;
  emptyTitle?: ReactNode;
  errorMessage?: string;
  getRowId?: (item: TData) => string;
  getRowProps?: (item: TData) => HTMLAttributes<HTMLTableRowElement>;
  isError?: boolean;
  isLoading?: boolean;
  loadingRowCount?: number;
  rowAction?: CleTableRowAction<TData>;
  selection?: CleTableSelection<TData>;
  selectedRowClassName?: string;
};

const mapSkeletonColumns = <TData,>(
  columns: CleTableColumn<TData>[],
): DocumentTableSkeletonColumn[] =>
  columns.map((column) => ({
    className: column.className,
    header: column.header,
    id: column.id,
  }));

const CleTable = <TData extends { id: string }>({
  columns,
  data,
  emptyDescription = "Når der er dokumenter, vises de her i oversigten.",
  emptyTitle = "Ingen dokumenter fundet",
  errorMessage,
  getRowId = (item) => item.id,
  getRowProps,
  isError = false,
  isLoading = false,
  loadingRowCount,
  rowAction,
  selection,
  selectedRowClassName = "cle-report-detail__document-row--selected",
}: CleTableProps<TData>) => {
  const selectAllInputRef = useRef<HTMLInputElement>(null);
  const rowIds = data.map(getRowId);
  const selectableRowIds = selection
    ? data
        .filter((item) => !(selection.isDisabled?.(item) ?? false))
        .map(getRowId)
    : rowIds;
  const selectedSelectableIds = selection
    ? selectableRowIds.filter((rowId) => selection.selectedIds.includes(rowId))
    : [];
  const hasSelectableRows = selectableRowIds.length > 0;
  const isAllSelected =
    selection != null &&
    hasSelectableRows &&
    selectedSelectableIds.length === selectableRowIds.length;
  const isPartiallySelected =
    selection != null &&
    selectedSelectableIds.length > 0 &&
    selectedSelectableIds.length < selectableRowIds.length;

  useEffect(() => {
    if (selectAllInputRef.current) {
      selectAllInputRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  if (isLoading) {
    return (
      <DocumentTableSkeleton
        columns={mapSkeletonColumns(columns)}
        rowCount={loadingRowCount}
        showSelectionColumn={selection != null}
      />
    );
  }

  if (isError) {
    return <TableError message={errorMessage} />;
  }

  if (data.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center p-3">
        <EmptyState
          icon={<FileText size={24} strokeWidth={1.75} />}
          title={emptyTitle}
          description={emptyDescription}
        />
      </div>
    );
  }

  const handleSelectAllChange = () => {
    if (!selection) {
      return;
    }

    if (selection.onSelectAll) {
      selection.onSelectAll(isAllSelected ? [] : selectableRowIds);

      return;
    }

    selectableRowIds.forEach((rowId) => {
      const isSelected = selection.selectedIds.includes(rowId);

      if (isAllSelected ? isSelected : !isSelected) {
        selection.onToggle(rowId);
      }
    });
  };

  const handleRowKeyDown = (
    event: KeyboardEvent<HTMLTableRowElement>,
    rowId: string,
    item: TData,
  ) => {
    const isActionDisabled = rowAction?.isDisabled?.(item) ?? false;
    const isSelectionDisabled = selection?.isDisabled?.(item) ?? false;

    if (
      (!selection || isSelectionDisabled) &&
      (!rowAction || isActionDisabled)
    ) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      if (selection && !isSelectionDisabled) {
        selection.onToggle(rowId);

        return;
      }

      rowAction?.onClick(rowId, item);
    }
  };

  return (
    <div className="cle-documents-table table-responsive border-start-0 border-end-0 rounded-0">
      <table className="cle-documents-table__table table table-hover align-middle mb-0">
        <thead>
          <tr>
            {selection ? (
              <th scope="col" className="cle-documents-table__selection-cell">
                <input
                  ref={selectAllInputRef}
                  className="form-check-input"
                  type="checkbox"
                  aria-label={
                    selection.selectAllAriaLabel ?? "Vælg alle dokumenter"
                  }
                  checked={isAllSelected}
                  disabled={!hasSelectableRows}
                  onChange={handleSelectAllChange}
                />
              </th>
            ) : null}
            {columns.map((column) => (
              <th key={column.id} scope="col" className={column.className}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const rowId = getRowId(item);
            const isSelected = selection?.selectedIds.includes(rowId) ?? false;
            const isSelectionDisabled = selection?.isDisabled?.(item) ?? false;
            const rowProps = getRowProps?.(item);
            const isActionDisabled = rowAction?.isDisabled?.(item) ?? false;
            const isInteractive =
              (selection != null && !isSelectionDisabled) ||
              (rowAction != null && !isActionDisabled);
            const rowActionClassName =
              typeof rowAction?.className === "function"
                ? rowAction.className(item)
                : rowAction?.className;
            const rowClassName = [
              rowProps?.className,
              isSelected ? selectedRowClassName : undefined,
              rowActionClassName,
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <tr
                {...rowProps}
                key={rowId}
                className={rowClassName}
                role={isInteractive ? "button" : rowProps?.role}
                tabIndex={isInteractive ? 0 : rowProps?.tabIndex}
                onClick={(event) => {
                  rowProps?.onClick?.(event);

                  if (event.defaultPrevented) {
                    return;
                  }

                  if (selection && !isSelectionDisabled) {
                    selection.onToggle(rowId);

                    return;
                  }

                  if (rowAction && !isActionDisabled) {
                    rowAction.onClick(rowId, item);
                  }
                }}
                onKeyDown={
                  isInteractive
                    ? (event) => {
                        rowProps?.onKeyDown?.(event);

                        if (event.defaultPrevented) {
                          return;
                        }

                        handleRowKeyDown(event, rowId, item);
                      }
                    : rowProps?.onKeyDown
                }
              >
                {selection ? (
                  <td
                    className="cle-documents-table__selection-cell"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      aria-label={selection.getAriaLabel(item)}
                      checked={isSelected}
                      disabled={isSelectionDisabled}
                      onChange={() => selection.onToggle(rowId)}
                    />
                  </td>
                ) : null}
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={column.className}
                    title={column.title?.(item)}
                  >
                    {column.cell(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CleTable;
